'use client';

import Script from 'next/script';
import { useEffect, useRef } from 'react';

interface ConsentModeDefaultsProps {
  analyticsGranted: boolean;
  marketingGranted: boolean;
}

/**
 * Google Consent Mode v2 integration.
 *
 * Sets `gtag('consent', 'default', ...)` via a beforeInteractive script so
 * that all Google tags (GA4, GTM, Google Ads) respect consent state from
 * the very first request. When the user updates their preferences the
 * component calls `gtag('consent', 'update', ...)` and toggles Meta's
 * `fbq('consent', ...)` accordingly.
 *
 * Required by Google since March 2024 for EEA traffic.
 */
export function ConsentModeDefaults({
  analyticsGranted,
  marketingGranted,
}: ConsentModeDefaultsProps) {
  const hasInitialised = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // On subsequent renders (consent changes), push an update
    if (hasInitialised.current) {
      window.gtag?.('consent', 'update', {
        analytics_storage: analyticsGranted ? 'granted' : 'denied',
        ad_storage: marketingGranted ? 'granted' : 'denied',
        ad_user_data: marketingGranted ? 'granted' : 'denied',
        ad_personalization: marketingGranted ? 'granted' : 'denied',
      });

      // Meta consent integration
      if (window.fbq) {
        window.fbq('consent', marketingGranted ? 'grant' : 'revoke');
      }
    }

    hasInitialised.current = true;
  }, [analyticsGranted, marketingGranted]);

  const analyticsDefault = analyticsGranted ? 'granted' : 'denied';

  return (
    <Script
      id="consent-mode-defaults"
      strategy="beforeInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          if (!window.gtag) { window.gtag = gtag; }

          gtag('consent', 'default', {
            'analytics_storage': '${analyticsDefault}',
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied',
            'functionality_storage': 'granted',
            'security_storage': 'granted',
            'ads_data_redaction': true,
            'url_passthrough': true,
            'wait_for_update': 500
          });
        `,
      }}
    />
  );
}
